/**
 * Instagram path segments that are never profile handles.
 */
const RESERVED_SEGMENTS = new Set([
  "p",
  "reel",
  "reels",
  "stories",
  "explore",
  "tv",
  "accounts",
]);

/**
 * Canonical Instagram handle from a pasted handle or profile URL, or "" when the
 * input carries no usable handle.
 *
 * Mirrors Tina's `normalize_handle` (tina/src/pipeline/extractors/config.py):
 * IG usernames are canonically lowercase, and Tina attributes scraped posts back
 * to a member site by normalized owner username — so the two MUST agree.
 *
 * Unlike the Python version — which takes the last path segment — this returns
 * "" for host-only and post/reel URLs instead of a bogus segment
 * ("www.instagram.com" for a host-only URL, the post shortcode for /p/<id>/), so
 * the form can reject them rather than create a site that scrapes nothing.
 */
export function normalizeInstagramHandle(value: string): string {
  const text = value.trim().toLowerCase();
  if (!text) return "";

  if (text.includes("instagram.com")) {
    const segments = text
      .split("?")[0]
      .split("#")[0]
      .split("/")
      .filter(Boolean);
    const hostIndex = segments.findIndex((s) => s.includes("instagram.com"));
    const segment = hostIndex >= 0 ? (segments[hostIndex + 1] ?? "") : "";
    if (!segment || RESERVED_SEGMENTS.has(segment)) return "";
    return segment.replace(/^@+/, "");
  }

  return text
    .replace(/^@+/, "")
    .replace(/^\/+|\/+$/g, "")
    .trim();
}
